import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Briefcase,
  Building2,
  Edit,
  Trash2,
  Sparkles,
  Linkedin
} from "lucide-react";
import EditContactDialog from "@/components/EditContactDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    fetchContactDetails();
  }, [id]);

  const fetchContactDetails = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${id}`, {
        withCredentials: true
      });
      setContact(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch contact details");
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    
    try {
      await axios.delete(`${API}/contacts/${id}`, {
        withCredentials: true
      });
      toast.success("Contact deleted successfully!");
      navigate("/contacts");
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const response = await axios.post(`${API}/contacts/${id}/enrich`, {}, {
        withCredentials: true
      });
      toast.success(`Enriched ${response.data.enriched_fields.length} fields!`);
      setContact(response.data.contact);
    } catch (error) {
      console.error("Error enriching contact:", error);
      toast.error(error.response?.data?.detail || "Failed to enrich contact");
    } finally {
      setEnriching(false);
    }
  };

  if (loading || !contact) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      <Button variant="outline" onClick={() => navigate('/contacts')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Contacts
      </Button>

      <div className="glass-card rounded-xl p-8">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
              {contact.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{contact.name}</h1>
              {contact.title && <p className="text-slate-600 text-lg mt-1">{contact.title}</p>}
              {contact.company?.name && (
                <Badge className="mt-2 bg-indigo-100 text-indigo-700">
                  <Building2 className="w-3 h-3 mr-1" />
                  {contact.company.name}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={handleEnrich}
              disabled={enriching}
              data-testid="enrich-contact-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {enriching ? "Enriching..." : "Enrich with AI"}
            </Button>
            <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-contact-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" onClick={handleDelete} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* AI Enriched Data Section */}
      {(contact.job_title || contact.linkedin_url || contact.department || contact.professional_bio) && (
        <Card className="glass-card border-2 border-indigo-100">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                AI-Enriched Professional Profile
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contact.job_title && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Job Title</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{contact.job_title}</p>
                </div>
              )}
              
              {contact.department && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Department</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{contact.department}</p>
                </div>
              )}
              
              {contact.linkedin_url && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-slate-500 mb-2">LinkedIn</p>
                  <a 
                    href={contact.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:underline text-sm font-medium"
                  >
                    View Profile →
                  </a>
                </div>
              )}
            </div>
            
            {contact.professional_bio && (
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <h3 className="text-sm font-semibold text-indigo-900 mb-2">Professional Bio</h3>
                <p className="text-indigo-800 text-sm leading-relaxed">{contact.professional_bio}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {contact.email && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-900">{contact.email}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.location.href = `mailto:${contact.email}`}
                >
                  Send Email
                </Button>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-900">{contact.phone}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.location.href = `tel:${contact.phone}`}
                >
                  Call
                </Button>
              </div>
            )}
            {contact.title && (
              <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-slate-400" />
                <span className="text-slate-900">{contact.title}</span>
              </div>
            )}
            {(contact.linkedin_url || contact.linkedin) && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <a href={contact.linkedin_url || contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                  LinkedIn Profile
                </a>
              </div>
            )}
            {!contact.email && !contact.phone && !contact.title && !(contact.linkedin_url || contact.linkedin) && (
              <p className="text-slate-500 text-sm">No basic information available. Click "Enrich with AI" to add details.</p>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Related Leads ({contact.leads?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {contact.leads && contact.leads.length > 0 ? (
              <div className="space-y-3">
                {contact.leads.map((lead) => (
                  <div 
                    key={lead.id} 
                    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <p className="font-semibold text-slate-900">{lead.raw_data?.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-slate-600">{lead.raw_data?.company}</p>
                      <Badge className={`text-xs ${
                        lead.stage === 'won' ? 'bg-green-100 text-green-700' :
                        lead.stage === 'lost' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {lead.stage}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No leads associated with this contact</p>
            )}
          </CardContent>
        </Card>
      </div>

      {contact.notes && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{contact.notes}</p>
          </CardContent>
        </Card>
      )}

      {showEdit && (
        <EditContactDialog 
          contact={contact} 
          open={showEdit} 
          onClose={() => setShowEdit(false)} 
          onUpdated={fetchContactDetails} 
        />
      )}
    </div>
  );
};

export default ContactDetails;
